import React from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge, PriorityBadge } from './StatusBadge';
import { cn } from '../../lib/utils';
import { Clock, MessageSquare, Paperclip, User } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export const TicketCard = ({ ticket, isStaff = false, className }) => {
  const linkPath = isStaff 
    ? `/backoffice/tickets/${ticket.id}` 
    : `/portal/tickets/${ticket.id}`;

  const timeAgo = ticket.created_at 
    ? formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true, locale: fr })
    : '';

  return (
    <Link
      to={linkPath}
      data-testid={`ticket-card-${ticket.id}`}
      className={cn(
        'ticket-card block bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-lg p-5',
        className
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="mono text-xs text-slate-500 dark:text-zinc-500">
              {ticket.ticket_number}
            </span>
            <StatusBadge status={ticket.status} />
            {ticket.priority === 'urgent' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                Urgent
              </span>
            )}
          </div>
          
          <h3 className="font-semibold text-slate-900 dark:text-white truncate mb-2">
            {ticket.title}
          </h3>
          
          <p className="text-sm text-slate-600 dark:text-zinc-400 line-clamp-2 mb-3">
            {ticket.description}
          </p>
          
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 dark:text-zinc-500">
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>{timeAgo}</span>
            </div>
            
            {ticket.message_count > 0 && (
              <div className="flex items-center gap-1">
                <MessageSquare className="w-3.5 h-3.5" />
                <span>{ticket.message_count}</span>
              </div>
            )}
            
            {ticket.attachments?.length > 0 && (
              <div className="flex items-center gap-1">
                <Paperclip className="w-3.5 h-3.5" />
                <span>{ticket.attachments.length}</span>
              </div>
            )}
            
            {isStaff && ticket.assigned_to_name && (
              <div className="flex items-center gap-1">
                <User className="w-3.5 h-3.5" />
                <span>{ticket.assigned_to_name}</span>
              </div>
            )}
            
            <PriorityBadge priority={ticket.priority} />
          </div>
        </div>
        
        {ticket.client_action_required && !isStaff && (
          <div className="flex-shrink-0">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              Action requise
            </span>
          </div>
        )}
      </div>
      
      {ticket.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-slate-100 dark:border-zinc-800">
          {ticket.tags.map(tag => (
            <span 
              key={tag}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </Link>
  );
};

export default TicketCard;
